import {
    Button,
    CardTemplate,
    CartProductCard,
    OrderProcessCard,
} from "@hatch-digital-nl/bravilor-storybook";
import { JSX, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppLocation } from "../../app/hooks";
import { ReactComponent as LogoBlack } from "../../common/svgs/logo-black.svg";
import { ReactComponent as HomeIcon } from "../../common/svgs/home.svg";
import { RouteEnum } from "../../common/enums/RouteEnum";
import { ThankYouLocationState } from "../../common/types/router";
import { mapOrderLineToDisplay } from "../checkoutPage/utils/mapOrderLineLabels";
import { useThankYouLabels } from "./hooks/useThankYouLabels";
import { useThankYouPageLoading } from "./hooks/useThankYouPageLoading";
import {
    ThankYouBackButtonSkeleton,
    ThankYouContentSkeleton,
} from "./components/ThankYouPageSkeletons";

export const CheckoutThankYouPageContainer = (): JSX.Element => {
    const labels = useThankYouLabels();
    const isPageLoading = useThankYouPageLoading();
    const navigate = useNavigate();
    const location = useAppLocation<ThankYouLocationState>();
    const order = location.state?.order;

    useEffect(() => {
        if (isPageLoading) {
            return;
        }

        if (!order?.order_lines?.length) {
            navigate(RouteEnum.PRODUCT_RANGE, { replace: true });
        }
    }, [order, navigate, isPageLoading]);

    const orderItems = useMemo(() => {
        if (!order?.order_lines.length) {
            return [];
        }

        return order.order_lines.map((line) =>
            mapOrderLineToDisplay(line, labels.refurbishedStatus),
        );
    }, [order, labels.refurbishedStatus]);

    const navigateToProductRange = (): void => {
        navigate(RouteEnum.PRODUCT_RANGE);
    };

    if (isPageLoading) {
        return (
            <div className="p-checkout-thank-you-page">
                <header className="p-checkout-thank-you-page__header">
                    <ThankYouBackButtonSkeleton />
                    <Link
                        to={RouteEnum.PRODUCT_RANGE}
                        className="p-checkout-thank-you-page__logo-link"
                        aria-label="Bravilor"
                    >
                        <LogoBlack className="p-checkout-thank-you-page__logo" />
                    </Link>
                </header>
                <ThankYouContentSkeleton />
            </div>
        );
    }

    if (!order?.order_lines?.length) {
        return <div className="p-checkout-thank-you-page" />;
    }

    return (
        <div className="p-checkout-thank-you-page">
            <header className="p-checkout-thank-you-page__header">
                <Button
                    variant="secondary-outline"
                    icon={HomeIcon}
                    onClick={navigateToProductRange}
                    className="p-checkout-thank-you-page__back-btn"
                >
                    {labels.backToB2b}
                </Button>
                <Link
                    to={RouteEnum.PRODUCT_RANGE}
                    className="p-checkout-thank-you-page__logo-link"
                    aria-label="Bravilor"
                >
                    <LogoBlack className="p-checkout-thank-you-page__logo" />
                </Link>
            </header>

            <div className="p-checkout-thank-you-page__content">
                <h1 className="p-checkout-thank-you-page__title">
                    {labels.title}
                </h1>

                <div className="p-checkout-thank-you-page__order-section">
                    <CardTemplate
                        title={labels.itemsInOrder}
                        className="p-checkout-thank-you-page__order-card"
                    >
                        <div className="p-checkout-thank-you-page__items">
                            {orderItems.map((item) => (
                                <CartProductCard
                                    key={item.id}
                                    id={item.id}
                                    name={item.name}
                                    image={item.image}
                                    status={item.status}
                                    grade={item.grade}
                                    price={item.price}
                                    originalPrice={item.originalPrice}
                                    serialNr={item.serialNumber}
                                    artNr={item.productIdentifier}
                                    priceLabel={labels.priceLabel}
                                    serialNrLabel={labels.serialNrLabel}
                                    artNrLabel={labels.artNrLabel}
                                />
                            ))}
                        </div>
                    </CardTemplate>
                </div>

                <section className="p-checkout-thank-you-page__process">
                    <h2 className="p-checkout-thank-you-page__process-title">
                        {labels.whatsNext}
                    </h2>
                    <div className="p-checkout-thank-you-page__process-cards">
                        <OrderProcessCard
                            variant="feedback"
                            step={1}
                            title={labels.step1Title}
                            description={labels.step1Description}
                        />
                        <OrderProcessCard
                            variant="feedback"
                            step={2}
                            title={labels.step2Title}
                            description={labels.step2Description}
                        />
                        <OrderProcessCard
                            variant="feedback"
                            step={3}
                            title={labels.step3Title}
                            description={labels.step3Description}
                        />
                    </div>
                </section>

                <Button
                    variant="tertiary"
                    icon={HomeIcon}
                    onClick={navigateToProductRange}
                    className="p-checkout-thank-you-page__cta"
                >
                    {labels.goBackToB2b}
                </Button>
            </div>
        </div>
    );
};
